import { applyThemeBrandTokens, resolveDeckThemeTokenValues } from '@digital-twins/deck-ui/foundations/theme/applyThemeBrandTokens'
import type { BrandSettings } from '@digital-twins/shared-types'
import type { DeckMetadata } from '@digital-twins/deck-ui/slides/contracts'

export type DeckBrandResolvedTheme = 'light' | 'dark'

function getDeckBrandSettingsFromMetadata(metadata?: DeckMetadata): BrandSettings | null {
  if (!metadata) {
    return null
  }

  const hasBrandTokens = Boolean(
    metadata.brandPrimaryColor ||
    metadata.brandSecondaryColor ||
    metadata.brandThemeConfig ||
    metadata.brandRoleGraph,
  )

  if (!hasBrandTokens) {
    return null
  }

  return {
    brandPrimaryColor: metadata.brandPrimaryColor ?? null,
    brandSecondaryColor: metadata.brandSecondaryColor ?? null,
    brandThemeConfig: metadata.brandThemeConfig ?? null,
    brandLogoUrl: metadata.brandLogoUrl ?? null,
    brandLogoUrlOnDark: metadata.brandLogoUrlOnDark ?? null,
    brandIconLogoUrl: null,
    brandDomain: null,
    brandRoleGraph: metadata.brandRoleGraph ?? null,
    brandRoleGraphVersion: metadata.brandRoleGraph?.version ?? null,
    brandRoleGraphHash: metadata.brandRoleGraph?.hash ?? null,
    brandFontFamily: (metadata.brandFontFamily ?? null) as BrandSettings['brandFontFamily'],
    brandHeadingFontFamily: (metadata.brandHeadingFontFamily ?? null) as BrandSettings['brandHeadingFontFamily'],
    themePresetId: null,
    themeOverrides: null,
    shareFooterText: null,
  }
}

function applyDeckFontAttributes(metadata?: DeckMetadata): () => void {
  const root = document.documentElement
  const previousValues = new Map<string, string | null>()

  function setTemporaryAttribute(name: string, value?: string | null) {
    if (!value) {
      return
    }
    previousValues.set(name, root.getAttribute(name))
    root.setAttribute(name, value)
  }

  setTemporaryAttribute('data-heading-font', metadata?.brandHeadingFontFamily)
  setTemporaryAttribute('data-font', metadata?.brandFontFamily)

  return () => {
    previousValues.forEach((previousValue, name) => {
      if (previousValue === null) {
        root.removeAttribute(name)
      } else {
        root.setAttribute(name, previousValue)
      }
    })
  }
}

export function applyDeckMetadataBranding(
  metadata: DeckMetadata | undefined,
  resolvedTheme: DeckBrandResolvedTheme,
): () => void {
  const restoreFonts = applyDeckFontAttributes(metadata)
  const brandSettings = getDeckBrandSettingsFromMetadata(metadata)
  const disposeTokens = brandSettings
    ? applyThemeBrandTokens(brandSettings, resolvedTheme, { scope: 'deck', restore: true })
    : undefined

  return () => {
    disposeTokens?.()
    restoreFonts()
  }
}

/**
 * The deck-scope colours this deck's brand renders with, for the PowerPoint
 * export (light, as the PDF render draws). A deck without a brand renders in the
 * default theme, which the export already has.
 */
export function deckThemeTokensForExport(metadata: DeckMetadata | undefined): Record<string, string> | undefined {
  const brandSettings = getDeckBrandSettingsFromMetadata(metadata)
  return brandSettings ? resolveDeckThemeTokenValues(brandSettings, 'light') : undefined
}
